import React from 'react'

import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { cn } from '@/shared/utils/helpers'

export interface SuccessModalAction {
	/** Button label */
	label: string
	/** Callback when button is clicked */
	onClick: () => void
	/** Button variant */
	variant?: 'default' | 'outline' | 'secondary' | 'ghost'
	/** Whether the button is in a loading state */
	isLoading?: boolean
	/** Loading text to show when isLoading is true */
	loadingText?: string
}

export interface SuccessModalProps {
	/** Whether the modal is visible */
	visible: boolean
	/** Image source to display at the top of the modal */
	image: string
	/** Alt text for the image */
	imageAlt?: string
	/** Modal title */
	title: string
	/** Modal description text */
	description: string
	/** Primary action button (right side / main CTA) */
	primaryAction?: SuccessModalAction
	/** Secondary action button (left side / alternative action) */
	secondaryAction?: SuccessModalAction
	/** Callback when modal is closed (X button or outside click) */
	onClose: () => void
	/** Whether to show the close button (default: true) */
	showCloseButton?: boolean
	/** Custom className for the modal content */
	className?: string
	/** Custom className for the image */
	imageClassName?: string
}

/**
 * Reusable success/informational modal with a prominent image.
 * Ideal for onboarding steps, success states, feature announcements, etc.
 *
 * Uses Dialog component following the app's design patterns.
 *
 * @example
 * ```tsx
 * <SuccessModal
 *   visible={isOpen}
 *   image={devicesImage}
 *   imageAlt="Devices preview"
 *   title="Tu cuenta está lista para comenzar"
 *   description="Usa la aplicación Brandtrack para reproducir música en tus dispositivos."
 *   primaryAction={{
 *     label: "Continuar",
 *     onClick: handleContinue,
 *   }}
 *   secondaryAction={{
 *     label: "Ver más",
 *     onClick: handleViewMore,
 *     variant: "outline",
 *   }}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
const SuccessModal: React.FC<SuccessModalProps> = ({
	visible,
	image,
	imageAlt = '',
	title,
	description,
	primaryAction,
	secondaryAction,
	onClose,
	showCloseButton = true,
	className,
	imageClassName,
}) => {
	return (
		<Dialog open={visible} onOpenChange={onClose}>
			<DialogContent
				className={cn(
					'sm:max-w-[600px] max-h-[90vh] p-0 gap-0 overflow-y-auto flex flex-col',
					className
				)}
				showCloseButton={showCloseButton}
				onInteractOutside={(e) => e.preventDefault()}
			>
				{/* Image Section */}
				<div className="w-full flex-shrink-0 flex items-center justify-center bg-secondary/50 p-6">
					<img
						src={image}
						alt={imageAlt}
						className={cn('max-w-full h-auto object-contain', imageClassName)}
					/>
				</div>

				{/* Content Section */}
				<div className="px-8 py-8 flex flex-col items-start text-left space-y-4 flex-shrink-0">
					<DialogTitle className="text-2xl font-semibold">{title}</DialogTitle>

					<DialogDescription className="text-base text-muted-foreground whitespace-pre-line">
						{description}
					</DialogDescription>

					{/* Actions */}
					{(primaryAction || secondaryAction) && (
						<div className="flex items-center justify-end gap-3 pt-4 w-full">
							{secondaryAction && (
								<Button
									type="button"
									variant={secondaryAction.variant || 'outline'}
									onClick={secondaryAction.onClick}
									disabled={secondaryAction.isLoading}
									className="rounded-full px-6 h-10"
								>
									{secondaryAction.isLoading
										? secondaryAction.loadingText || secondaryAction.label
										: secondaryAction.label}
								</Button>
							)}
							{primaryAction && (
								<Button
									type="button"
									variant={primaryAction.variant || 'default'}
									onClick={primaryAction.onClick}
									disabled={primaryAction.isLoading}
									className="rounded-full bg-white text-black hover:bg-gray-100 px-6 h-10"
								>
									{primaryAction.isLoading
										? primaryAction.loadingText || primaryAction.label
										: primaryAction.label}
								</Button>
							)}
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default SuccessModal
